import { MoreVertical } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface QcMoreActionsMenuProps {
  /** Whether the "Show only ineligible evaluations" view is active. */
  onlyIneligible: boolean;
  /** Called with the next value when the toggle is flipped. */
  onOnlyIneligibleChange: (next: boolean) => void;
  /** Whether the "Show only reviewed evaluations" view is active (AQ-499). */
  onlyReviewed: boolean;
  /** Called with the next value when the reviewed toggle is flipped. */
  onOnlyReviewedChange: (next: boolean) => void;
}

/**
 * The QC list toolbar's 3-dot "more actions" menu. Exposes two toggles that narrow
 * the list and tab counts: "Show only ineligible evaluations" (AQ-653) and
 * "Show only reviewed evaluations" (AQ-499). Both can be on at once (AND).
 */
export const QcMoreActionsMenu: React.FC<QcMoreActionsMenuProps> = ({
  onlyIneligible,
  onOnlyIneligibleChange,
  onlyReviewed,
  onOnlyReviewedChange,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10"
        aria-label="More actions"
        data-analytics-id="qc:list:more-actions:open"
      >
        <MoreVertical className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-[320px]">
      <DropdownMenuCheckboxItem
        checked={onlyIneligible}
        onCheckedChange={checked => onOnlyIneligibleChange(!!checked)}
        // Keep the menu open on toggle so the helper text stays visible.
        onSelect={event => event.preventDefault()}
        className="items-start gap-2"
      >
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-foreground">Show only ineligible evaluations</span>
          <span className="text-xs text-muted-foreground">
            Calls the AI judged to be unscorable. Click to view their transcripts and recordings.
          </span>
        </div>
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={onlyReviewed}
        onCheckedChange={checked => onOnlyReviewedChange(!!checked)}
        onSelect={event => event.preventDefault()}
        className="items-start gap-2"
        data-analytics-id="qc:list:more-actions:only-reviewed"
      >
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-foreground">Show only reviewed evaluations</span>
          <span className="text-xs text-muted-foreground">
            Calls where a reviewer updated an answer, flag or metadata.
          </span>
        </div>
      </DropdownMenuCheckboxItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
